// src/views/litellm/RealtimeAIMonitorPage.jsx
import React, { useCallback, useMemo, useState } from "react";
import { useMonitorSummary, useMonitorEvents, useAiCostSummary } from "./hooks";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import FreshnessLabel from "../../components/ui-components/FreshnessLabel";
import HealthStrip from "./components/HealthStrip";
import AiCostBasisStrip from "./components/AiCostBasisStrip";
import HourlyChart from "./components/HourlyChart";
import MonitorFilterBar from "./components/MonitorFilterBar";
import TopStatsTable from "./components/TopStatsTable";
import RecentEventsFeed from "./components/RecentEventsFeed";
import { clockTime, ageLabel } from "./format";

const EVENTS_LIMIT = 50;

const EMPTY_SUMMARY = {
  window: {},
  freshness: {},
  totals: {},
  active: {},
  hourly: [],
  top_api_keys: [],
  top_models: [],
};

// Chip is a removable active-filter pill.
function Chip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-surface-2 text-text-2">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="text-text-3 hover:text-text">
        ×
      </button>
    </span>
  );
}

export default function RealtimeAIMonitorPage() {
  // Drill-down + chart filter; empty object = today, hourly, unfiltered. Holds source_key,
  // model, hours (period), and granularity ("minute" → per-minute buckets).
  const [filter, setFilter] = useState({});

  // Chart view state, lifted here so the header filter bar can drive it. Unlike `filter` these
  // don't refetch — they only change how the same data is drawn (which series, whose clock,
  // and whether the running total is overlaid).
  const [metricKey, setMetricKey] = useState("cost");
  const [tz, setTz] = useState("Local");
  const [cumulative, setCumulative] = useState(false);

  const summaryQ = useMonitorSummary(filter);
  const eventsQ = useMonitorEvents(filter, EVENTS_LIMIT);

  // AI cost re-basis proof tile — actual vs API-equivalent (shadow) vs effective discount for
  // the current month. Independent of the day-level monitor filters above.
  const currentPeriod = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const aiCostQ = useAiCostSummary(currentPeriod);

  // Dropdown options should stay stable when a model/key is selected (a filtered summary only
  // returns the selected dimension). When a dimension filter is active, source the options from
  // a period-only summary; otherwise the main (dim-unfiltered) summary already lists them all.
  const dimFiltered = Boolean(filter.model || filter.source_key);
  const optionsFilter = useMemo(() => (filter.hours ? { hours: filter.hours } : {}), [filter.hours]);
  const optionsQ = useMonitorSummary(optionsFilter, { enabled: dimFiltered });

  const summary = summaryQ.data || EMPTY_SUMMARY;
  const events = eventsQ.data?.events || [];
  const isFetching = summaryQ.isFetching || eventsQ.isFetching;

  const optSource = dimFiltered ? optionsQ.data || EMPTY_SUMMARY : summary;
  const modelOptions = useMemo(
    () => (optSource.top_models || []).map((r) => r.model).filter(Boolean),
    [optSource]
  );
  const keyOptions = useMemo(
    () => (optSource.top_api_keys || []).map((r) => ({ value: r.source_key, label: r.display })),
    [optSource]
  );

  const refreshAll = () => {
    summaryQ.refetch();
    eventsQ.refetch();
  };

  // updateFilter merges a patch into the filter, dropping keys set to undefined (so absent =
  // unfiltered/default rather than an explicit empty value).
  const updateFilter = useCallback(
    (patch) =>
      setFilter((f) => {
        const next = { ...f, ...patch };
        for (const k of Object.keys(next)) {
          if (next[k] === undefined || next[k] === "") delete next[k];
        }
        return next;
      }),
    []
  );

  const setModel = (model) => updateFilter({ model });
  const setSourceKey = (source_key) => updateFilter({ source_key });
  const clearKey = (k) => updateFilter({ [k]: undefined });

  const lastUpdated = useMemo(
    () => (summaryQ.dataUpdatedAt ? clockTime(new Date(summaryQ.dataUpdatedAt).toISOString()) : null),
    [summaryQ.dataUpdatedAt]
  );

  // Only the genuine cold-start (no filters, nothing ingested) shows the full-page empty state.
  // Once any filter is active, keep the chart mounted so its controls stay reachable even when
  // the filtered window has no data (it renders zero bars instead of vanishing).
  const hasFilter = Boolean(filter.source_key || filter.model || filter.hours || filter.granularity);
  const isEmpty =
    !summaryQ.isLoading &&
    !hasFilter &&
    (summary.totals?.events || 0) === 0 &&
    summary.freshness?.latest_ingest_ts == null;

  // Header freshness sits beside Refresh (row 1), not in a near-empty row of its own. It reads as
  // the read-only "last pulled" stamp when data is live, but switches to a semantic amber warning
  // the moment ingestion breaches the freshness threshold (events exist, none in the last 10 min) —
  // the one state worth colouring, since every number on the page is then suspect.
  const fr = summary.freshness || {};
  const staleHeader = fr.latest_ingest_ts != null && !fr.flowing;
  const headerFreshness = staleHeader ? (
    <span className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-warning select-none">
      Stale · last event {ageLabel(fr.seconds_since_latest)}
    </span>
  ) : lastUpdated ? (
    <FreshnessLabel>Updated {lastUpdated} UTC</FreshnessLabel>
  ) : null;

  return (
    <div className="p-6 space-y-6">
      <HeaderPortal slot="actions">
        <div className="flex items-center gap-3">
          {headerFreshness}
          <button type="button" onClick={refreshAll} disabled={isFetching} className={primaryButtonCls}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </HeaderPortal>

      {summaryQ.error || eventsQ.error ? (
        <div className="bg-danger-soft text-danger rounded-card p-4 text-sm">
          Failed to load monitor data. <button type="button" className="underline" onClick={refreshAll}>Retry</button>
        </div>
      ) : (
        <>
          {/* Analysis controls live in the global header's filter row (like /cost-analyzer), not on
              the chart. Hidden only on the genuine cold-start, when there's nothing to filter. */}
          {!isEmpty && (
            <HeaderPortal slot="row3-filters">
              <MonitorFilterBar
                filter={filter}
                onFilterChange={updateFilter}
                modelOptions={modelOptions}
                keyOptions={keyOptions}
                metricKey={metricKey}
                setMetricKey={setMetricKey}
                tz={tz}
                setTz={setTz}
                cumulative={cumulative}
                setCumulative={setCumulative}
              />
            </HeaderPortal>
          )}

          {/* Active drill-down chips */}
          {(filter.source_key || filter.model) && (
            <div className="flex items-center gap-2 flex-wrap">
              {filter.source_key && <Chip label={`Key: ${filter.source_key}`} onRemove={() => clearKey("source_key")} />}
              {filter.model && <Chip label={`Model: ${filter.model}`} onRemove={() => clearKey("model")} />}
            </div>
          )}

          <HealthStrip freshness={summary.freshness} totals={summary.totals} active={summary.active} />

          <AiCostBasisStrip aiCost={aiCostQ.data} loading={aiCostQ.isLoading} />

          {isEmpty ? (
            <div className="bg-card rounded-card border border-card-bd p-8 text-center text-text-3">
              No AI events ingested today yet.
            </div>
          ) : (
            <>
              <HourlyChart
                hourly={summary.hourly}
                from={summary.window?.from}
                to={summary.window?.to}
                filter={filter}
                metricKey={metricKey}
                tz={tz}
                cumulative={cumulative}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TopStatsTable
                  title="Top API keys"
                  nameHeader="API key"
                  rows={summary.top_api_keys}
                  getName={(r) => r.display}
                  getFilterValue={(r) => r.source_key}
                  selectedValue={filter.source_key}
                  onSelect={setSourceKey}
                />
                <TopStatsTable
                  title="Top models"
                  nameHeader="Model"
                  rows={summary.top_models}
                  getName={(r) => r.model}
                  getFilterValue={(r) => r.model}
                  selectedValue={filter.model}
                  onSelect={setModel}
                />
              </div>

              <RecentEventsFeed events={events} />
            </>
          )}
        </>
      )}
    </div>
  );
}
